import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../Components/Toast";
import Button from "../../Components/Button";
import FormCard from "../../Components/FormCard";
import TextboxSet from "../../Components/TextboxSet";
import DropdownSet from "../../Components/DropdownSet";
import Breadcrumb from "../../Components/Breadcrumb";
import Header from "../../Components/Header";
import LoadingInComponent from "../../Components/LoadingInComponent";
import {
  CREATE_USER_API,
  EVENT_OPTIONS_API,
  ROLE_OPTIONS_API,
} from "../../Constant/api";

const UserCreate = () => {
  const [loading, setLoading] = useState(false);
  const [getUserInfo, setUserInfo] = useState({
    name: "",
    email: "",
    password: "",
    event_id: "",
    role_id: "",
  });
  const [getEventOptions, setEventOptions] = useState([]);
  const [getRoleOptions, setRoleOptions] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchEventList = useCallback(() => {
    setLoading(true);
    axios
      .get(EVENT_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((res) => {
        setEventOptions(res.data.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast("Failed to fetch event list.", "error");
      });
  }, [toast]);

  const fetchRoleList = useCallback(() => {
    setLoading(true);
    axios
      .get(ROLE_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((res) => {
        setRoleOptions(res.data.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast("Failed to fetch role list.", "error");
      });
  }, [toast]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropDownChange = (name, value) => {
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!emailRegex.test(getUserInfo.email)) {
      toast("Please enter a valid email address.", "error");
      return;
    }

    setLoading(true);
    try {
      axios
        .post(CREATE_USER_API, getUserInfo, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        })
        .then(() => {
          setLoading(false);
          toast("User created successfully.", "success");
          navigate("/admin/user-list");
        })
        .catch((err) => {
          setLoading(false);
          toast(
            err.response?.data?.message || "Failed to create user.",
            "error",
          );
        });
    } catch (error) {
      setLoading(false);
      console.log(error);
      toast(error.message || "Failed to create user.", "error");
    }
  };

  useEffect(() => {
    fetchEventList();
    fetchRoleList();
  }, [fetchEventList, fetchRoleList]);

  const breadcrumbItems = [
    { label: "User List", to: "/admin/user-list" },
    { label: "Create" },
  ];

  return (
    <div className="flex flex-col w-full h-full">
      <Header noSidebar={true} />
      <LoadingInComponent isLoading={loading}>
        {!loading && (
          <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[24px]">
            <Breadcrumb items={breadcrumbItems} />

            <h1 className="text-[29px]">Create User</h1>

            <div className="flex flex-col md:flex-row w-full gap-[24px]">
              <FormCard title="User Information">
                <div className="grid grid-cols-1 lg:grid-cols-2 w-full gap-[24px] p-[24px]">
                  <TextboxSet
                    type="text"
                    name="name"
                    label="Name"
                    value={getUserInfo.name}
                    required
                    onChange={handleOnChange}
                  />
                  <TextboxSet
                    type="email"
                    name="email"
                    label="Email"
                    value={getUserInfo.email}
                    required
                    onChange={handleOnChange}
                  />

                  <TextboxSet
                    type="password"
                    name="password"
                    label="Password"
                    value={getUserInfo.password}
                    required
                    onChange={handleOnChange}
                  />

                  <DropdownSet
                    name="event_id"
                    label="Event"
                    value={getUserInfo.event_id}
                    options={getEventOptions}
                    onChange={handleDropDownChange}
                  />
                  <div className="lg:col-span-2">
                    <DropdownSet
                      name="role_id"
                      label="Role"
                      value={getUserInfo.role_id}
                      options={getRoleOptions}
                      required
                      onChange={handleDropDownChange}
                    />
                  </div>
                </div>
              </FormCard>
            </div>

            <div className="flex gap-[8px]">
              <Button onClick={handleCreate} loading={loading}>
                Create
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate("/admin/user-list")}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default UserCreate;
